// src/scenes/contracts/ContractFormPage/ClienteTab.jsx
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useRef } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from '../../../hooks/useTranslation';
import { greenColor } from '../../../config/colors';
import { formatCpfCnpj, formatPhone, formatCep } from '../../../utils';
import CustomTable from '../../../components/CustomTable';

const TAX_REGIMES = [
  { value: 'SIMPLES', label: 'Simples Nacional' },
  { value: 'PRESUMIDO', label: 'Lucro Presumido' },
  { value: 'REAL', label: 'Lucro Real' },
  { value: 'MEI', label: 'MEI' },
];

const fieldLabelSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#9e9e9e',
  mb: 0.5,
  letterSpacing: '0.5px',
};

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: '13px',
  mb: 2,
  letterSpacing: '0.5px',
  color: '#333',
};

const inputSx = {
  '& .MuiInputBase-root': { fontSize: '14px', backgroundColor: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
};

const selectSx = {
  backgroundColor: '#fff',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
};

const LabeledField = ({ label, children }) => (
  <Box>
    <Typography sx={fieldLabelSx}>{label}</Typography>
    {children}
  </Box>
);

// Área de upload para um único documento
const DocumentUploadArea = ({ label, file, onSelect, onRemove, error }) => {
  const ref = useRef(null);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#555', mb: 1 }}>
        {label}
      </Typography>
      <Box
        sx={{
          border: `1px dashed ${error ? '#ef5350' : '#bdbdbd'}`,
          borderRadius: 2,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: file ? '#f9fbe7' : '#fafafa',
          cursor: file ? 'default' : 'pointer',
          transition: 'background-color 0.2s',
          '&:hover': !file ? { backgroundColor: '#f0f4f0' } : {},
        }}
        onClick={() => !file && ref.current?.click()}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: '#e8f5e9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AttachFileIcon sx={{ color: greenColor, fontSize: 18 }} />
          </Box>
          <Box>
            {file ? (
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
                {file.name}
              </Typography>
            ) : (
              <>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>
                  Anexar documento
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#9e9e9e' }}>
                  Envie um arquivo (PDF, JPG, PNG)
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {file ? (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            sx={{ color: '#ef5350' }}
            aria-label="Remover arquivo"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <AddIcon sx={{ color: greenColor, fontSize: 20 }} />
        )}

        <input
          ref={ref}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onSelect(f);
            e.target.value = '';
          }}
        />
      </Box>
      {error && (
        <Typography sx={{ fontSize: '11px', color: '#ef5350', mt: 0.5 }}>
          Documento obrigatório
        </Typography>
      )}
    </Box>
  );
};

const ClienteTab = ({ formData, onChange, errors }) => {
  const { t } = useTranslation();
  const s = 'contractForm.cliente';

  const handle = (field) => (e) => onChange({ [field]: e.target.value });

  const handleCpfCnpj = (field) => (e) => {
    onChange({ [field]: formatCpfCnpj(e.target.value) });
  };

  const handlePhone = (field) => (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
    onChange({ [field]: formatPhone(digits) });
  };

  const handleCep = (field) => (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    onChange({ [field]: formatCep(digits) });
  };

  const socios = formData.socios ?? [{ id: Date.now(), nome: '', email: '', telefone: '', cpf: '', rg: '' }];

  // Update sócio field by ID (used in CustomTable col.render closures)
  const handleSocioById = (id, field, value) => {
    const updated = socios.map(s => s.id === id ? { ...s, [field]: value } : s);
    onChange({ socios: updated });
  };

  const handleSocioCpfById = (id, value) => {
    handleSocioById(id, 'cpf', formatCpfCnpj(value));
  };

  const handleSocioPhoneById = (id, value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    handleSocioById(id, 'telefone', formatPhone(digits));
  };

  const handleAddSocio = () => {
    onChange({
      socios: [...socios, { id: Date.now(), nome: '', email: '', telefone: '', cpf: '', rg: '' }],
    });
  };

  const handleRemoveSocioById = (id) => {
    const updated = socios.filter(s => s.id !== id);
    onChange({
      socios: updated.length ? updated : [{ id: Date.now(), nome: '', email: '', telefone: '', cpf: '', rg: '' }],
    });
  };

  // Build CustomTable columns — defined inside component to capture handlers via closure
  const sociosColumns = [
    {
      field: 'nome',
      headerName: t(`${s}.partnerName`),
      render: (value, row) => (
        <TextField
          value={value || ''}
          onChange={(e) => handleSocioById(row.id, 'nome', e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder="Nome completo"
          inputProps={{ style: { fontSize: '14px' } }}
        />
      ),
    },
    {
      field: 'email',
      headerName: t(`${s}.partnerEmail`),
      render: (value, row) => (
        <TextField
          value={value || ''}
          onChange={(e) => handleSocioById(row.id, 'email', e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder="email@empresa.com"
          inputProps={{ style: { fontSize: '14px' } }}
        />
      ),
    },
    {
      field: 'telefone',
      headerName: t(`${s}.partnerPhone`),
      render: (value, row) => (
        <TextField
          value={value || ''}
          onChange={(e) => handleSocioPhoneById(row.id, e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder="(00) 00000-0000"
          inputProps={{ style: { fontSize: '14px' } }}
        />
      ),
    },
    {
      field: 'cpf',
      headerName: t(`${s}.partnerCpf`),
      render: (value, row) => (
        <TextField
          value={value || ''}
          onChange={(e) => handleSocioCpfById(row.id, e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder="000.000.000-00"
          inputProps={{ style: { fontSize: '14px' } }}
        />
      ),
    },
    {
      field: 'rg',
      headerName: t(`${s}.partnerRg`),
      render: (value, row) => (
        <TextField
          value={value || ''}
          onChange={(e) => handleSocioById(row.id, 'rg', e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder="0000000"
          inputProps={{ style: { fontSize: '14px' } }}
        />
      ),
    },
  ];

  // Only show delete action when there is more than one sócio
  const sociosActions = socios.length > 1
    ? [{
        icon: <DeleteOutlineIcon fontSize="small" sx={{ color: '#ef5350' }} />,
        tooltip: 'Remover sócio',
        onClick: (row) => handleRemoveSocioById(row.id),
      }]
    : [];

  return (
    <Box>
      {/* DADOS CADASTRAIS */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.cadastralData`)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <LabeledField label={t(`${s}.name`)}>
              <TextField
                value={formData.clienteNome}
                onChange={handle('clienteNome')}
                size="small"
                fullWidth
                error={!!errors?.clienteNome}
                helperText={errors?.clienteNome ? t('contractForm.validation.required') : ''}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledField label={t(`${s}.email`)}>
              <TextField
                value={formData.clienteEmail}
                onChange={handle('clienteEmail')}
                size="small"
                fullWidth
                error={!!errors?.clienteEmail}
                helperText={errors?.clienteEmail ? t('contractForm.validation.required') : ''}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledField label={t(`${s}.phone`)}>
              <TextField
                value={formData.clienteTelefone}
                onChange={handlePhone('clienteTelefone')}
                size="small"
                fullWidth
                placeholder="(00) 00000-0000"
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledField label={t(`${s}.cpfCnpj`)}>
              <TextField
                value={formData.clienteCpfCnpj}
                onChange={handleCpfCnpj('clienteCpfCnpj')}
                size="small"
                fullWidth
                placeholder="000.000.000-00"
                error={!!errors?.clienteCpfCnpj}
                helperText={errors?.clienteCpfCnpj ? t('contractForm.validation.required') : ''}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Box>

      {/* DADOS EMPRESARIAIS */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.businessData`)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LabeledField label={t(`${s}.razaoSocial`)}>
              <TextField
                value={formData.razaoSocial}
                onChange={handle('razaoSocial')}
                size="small"
                fullWidth
                error={!!errors?.razaoSocial}
                helperText={errors?.razaoSocial ? t('contractForm.validation.required') : ''}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.municipalRegistration`)}>
              <TextField
                value={formData.inscricaoMunicipal}
                onChange={handle('inscricaoMunicipal')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.stateRegistration`)}>
              <TextField
                value={formData.inscricaoEstadual}
                onChange={handle('inscricaoEstadual')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.taxRegime`)}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.regimeTributario}
                  onChange={handle('regimeTributario')}
                  displayEmpty
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em style={{ fontStyle: 'normal', color: '#9e9e9e' }}>Selecione</em>
                  </MenuItem>
                  {TAX_REGIMES.map((r) => (
                    <MenuItem key={r.value} value={r.value} sx={{ fontSize: '14px' }}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.responsibleEmail`)}>
              <TextField
                value={formData.emailResponsavel}
                onChange={handle('emailResponsavel')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.responsiblePhone`)}>
              <TextField
                value={formData.telefoneResponsavel}
                onChange={handlePhone('telefoneResponsavel')}
                size="small"
                fullWidth
                placeholder="(00) 00000-0000"
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.addressCnpj`)}>
              <TextField
                value={formData.enderecoRua}
                onChange={handle('enderecoRua')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.cep`)}>
              <TextField
                value={formData.cep}
                onChange={handleCep('cep')}
                size="small"
                fullWidth
                placeholder="00000-000"
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.state`)}>
              <TextField
                value={formData.estado}
                onChange={handle('estado')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.city`)}>
              <TextField
                value={formData.cidade}
                onChange={handle('cidade')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Box>

      {/* SÓCIOS — usando CustomTable com col.render() para campos editáveis */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.partners`)}</Typography>
        <CustomTable
          columns={sociosColumns}
          rows={socios}
          rowKey="id"
          actions={sociosActions}
          hidePagination
          elevation={0}
          containerSx={{ borderRadius: 1 }}
          emptyMessage="Nenhum sócio adicionado."
        />
        <Typography
          onClick={handleAddSocio}
          sx={{
            fontSize: '13px',
            color: greenColor,
            fontWeight: 600,
            cursor: 'pointer',
            mt: 2,
            display: 'inline-flex',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {t(`${s}.addPartner`)}
        </Typography>
      </Box>

      {/* DOCUMENTOS DO CLIENTE — obrigatórios Grafeno */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.documents`)}</Typography>

        <DocumentUploadArea
          label={t(`${s}.docComprovanteEndereco`)}
          file={formData.docComprovanteEndereco}
          onSelect={(f) => onChange({ docComprovanteEndereco: f })}
          onRemove={() => onChange({ docComprovanteEndereco: null })}
          error={!!errors?.docComprovanteEndereco}
        />
        <DocumentUploadArea
          label={t(`${s}.docFaturamento`)}
          file={formData.docFaturamento12meses}
          onSelect={(f) => onChange({ docFaturamento12meses: f })}
          onRemove={() => onChange({ docFaturamento12meses: null })}
          error={!!errors?.docFaturamento12meses}
        />
        <DocumentUploadArea
          label={t(`${s}.docContratoSocial`)}
          file={formData.docContratoSocial}
          onSelect={(f) => onChange({ docContratoSocial: f })}
          onRemove={() => onChange({ docContratoSocial: null })}
          error={!!errors?.docContratoSocial}
        />
      </Box>
    </Box>
  );
};

export default ClienteTab;
